import React from "react"
import { ChevronDown, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { DownloadIcon, type DownloadIconHandle } from "@/components/ui/download"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { DrawerIconButtonTooltip } from "../../Workspace/WorkspaceDrawerActionRow"

export interface DashboardHeaderExportMenuOption {
  id: string
  label: string
  onSelect: () => void
  disabled?: boolean
}

export interface DashboardHeaderExportButtonProps {
  onClick?: () => void
  disabled?: boolean
  isLoading?: boolean
  loadingLabel?: string
  label?: string
  /** Icon square only (e.g. workspace node drawer action rows). */
  iconOnly?: boolean
  className?: string
  "aria-label"?: string
  menuOptions?: DashboardHeaderExportMenuOption[]
}

/**
 * Dashboard / Trade Log inset header Export — shadcn `Button` outline-brand + animated `DownloadIcon`.
 */
export function DashboardHeaderExportButton({
  onClick,
  disabled = false,
  isLoading = false,
  loadingLabel = "Exporting...",
  label = "Export",
  iconOnly = false,
  className,
  "aria-label": ariaLabel = "Export trades",
  menuOptions,
}: DashboardHeaderExportButtonProps): React.ReactElement {
  const iconRef = React.useRef<DownloadIconHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    if (isLoading) return
    void iconRef.current?.startAnimation()
  }, [isLoading])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const iconHoverHandlers = React.useMemo(
    () =>
      isLoading
        ? {}
        : {
            onPointerDownCapture: startIconAnimation,
            onMouseEnter: startIconAnimation,
            onMouseLeave: stopIconAnimation,
          },
    [isLoading, startIconAnimation, stopIconAnimation]
  )

  const triggerButton = (
    <Button
      type="button"
      variant="outline-brand"
      size={iconOnly ? "icon" : "sm"}
      data-font="primary"
      className={cn(
        iconOnly
          ? "h-8 w-8 shrink-0"
          : "h-8 shrink-0 justify-between gap-1.5 !text-sm font-normal normal-case tracking-normal",
        menuOptions?.length && "!text-[color:var(--brand-tropical-surf)]",
        className
      )}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
      onClick={menuOptions?.length ? undefined : onClick}
      aria-label={
        isLoading && iconOnly
          ? loadingLabel
          : ariaLabel
      }
      {...iconHoverHandlers}
    >
      {iconOnly ? (
        isLoading ? (
          <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
        ) : (
          <DownloadIcon
            ref={iconRef}
            size={16}
            className="shrink-0 text-current"
            aria-hidden
          />
        )
      ) : (
        <>
          <span className="flex min-w-0 items-center gap-1.5">
            {isLoading ? (
              <Loader2
                data-icon="inline-start"
                className="size-4 shrink-0 animate-spin"
                aria-hidden
              />
            ) : (
              <DownloadIcon
                ref={iconRef}
                data-icon="inline-start"
                size={16}
                className="shrink-0 text-current"
                aria-hidden
              />
            )}
            <span
              data-font="primary"
              className={cn(
                "truncate !text-sm font-[family-name:var(--font-primary)]",
                menuOptions?.length && "!text-[color:var(--brand-tropical-surf)]"
              )}
            >
              {isLoading ? loadingLabel : label}
            </span>
          </span>
          {menuOptions?.length ? (
            <ChevronDown data-icon="inline-end" className="size-4 shrink-0 opacity-80" aria-hidden />
          ) : null}
        </>
      )}
    </Button>
  )

  if (menuOptions?.length) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {menuOptions.map((option) => (
            <DropdownMenuItem
              key={option.id}
              className="!text-sm !leading-5 !font-medium !font-ibm-plex !text-[color:var(--brand-tropical-surf)]"
              disabled={Boolean(option.disabled) || isLoading}
              onSelect={(event) => {
                event.preventDefault()
                option.onSelect()
              }}
            >
              <span
                data-font="primary"
                className="!text-sm !leading-5 !font-medium !font-[family-name:var(--font-primary)] !text-[color:var(--brand-tropical-surf)]"
              >
                {option.label}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (iconOnly) {
    return (
      <DrawerIconButtonTooltip label={isLoading ? loadingLabel : ariaLabel}>
        {triggerButton}
      </DrawerIconButtonTooltip>
    )
  }

  return triggerButton
}
